import React, { useState, useContext } from 'react'
import { Box, Button, Typography, CircularProgress } from '@mui/material'
import PrimaryPage from '../../Layout/PrimaryPage'
import { BuildDropdown, BuildTextField } from '../../Components/Inputs'
import CsvDialog from '../../Components/Dialogs'
import UserContext from '../../services/userService'
import { isValidEmail } from '../../shared/emailValidation'
import { useNavigate } from 'react-router-dom';

const INVALID_EMAIL_MESSAGE = 'Enter a valid email address.'
const INVITE_FAILED_MESSAGE = 'Something went wrong sending the invitation. Please try again.'

// inviteUser also re-throws whatever the AWS SDK failed with, so anything not on this list
// could be an internal exception (IAM denial, Cognito fault) whose text must not reach the UI.
const SAFE_INVITE_ERROR_MESSAGES = new Set([
  'An email address is required to invite a user.',
  'A user with this email address already exists for this account.',
  'A deactivated user with this email address already exists for this account.',
  'This email address is already on more than one user record for this account, so it cannot be invited again until those records are merged. Please contact support.',
  'Unauthorized! Only Admin users can invite users',
])

// Amplify v6 rejects a failed mutation with a { data, errors } object rather than an Error,
// so the backend's own message (duplicate user, deactivated user, unauthorized, ...) is only
// reachable through that array — a generic top-level message would mask it.
const resolveInviteErrorMessage = (error) => {
  const message = error?.errors?.[0]?.message || error?.message
  return SAFE_INVITE_ERROR_MESSAGES.has(message) ? message : INVITE_FAILED_MESSAGE
}

// The browser console is as exposed as the dialog, so the rejected object - which carries
// IAM/Cognito internals and the invitee's details - must not be logged. These two tags say
// which branch resolveInviteErrorMessage took, which is all the raw log was useful for.
const KNOWN_FAILURE_LOG = 'invite failed: known reason'
const UNEXPECTED_FAILURE_LOG = 'invite failed: unexpected error'

const InviteUser = () => {
  return (
    <PrimaryPage
      headerTitle="Add a new user"
      headerWidget={(
        <DescriptionSection/>
      )}
      bodyTitle="Who are you inviting?">
        <InviteUserSection/>
    </PrimaryPage>
  )
}

const DescriptionSection = () => {
  return (
    <Typography alignItems='left' sx={{
      fontSize: '1rem',
      wordWrap: 'break-word',
      marginBottom: '1.5rem',
    }}>
      Invite a user to take part in verifying professional licenses utilizing Mesh.
    </Typography>
  )
}

const InviteUserSection = () => {
  const service = useContext(UserContext)
  const navigate = useNavigate()
  const [isInviting, setIsInviting] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [newUser, setNewUser] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const[filters, setFilters] = useState({
    email: '',
    role: '',
  })

  const emailError = filters.email !== '' && !isValidEmail(filters.email)

  const roleValues = [
    {
      value: '',
      label: 'Select roles',
    },
    {
      value: 'MEMBER',
      label: 'User',
    },
    {
      value: 'ADMIN',
      label: 'Super User',
    },
  ]

  const filterOnChange = async (key, e) => {
    if(key.includes('email'))
      setFilters((oldState) => ({
          ...oldState,
          email: e,
      }))
    if(key.includes('role'))
      setFilters((oldState) => ({
          ...oldState,
          role: e,
      }))
  }

  const inviteUser = async () => {
    if(!isValidEmail(filters.email)) {
      return
    }
    setIsInviting(true)
    try {
      const invitedUser = await service.invite(filters)
      setIsOpen(true)
      setNewUser(invitedUser)
    } catch (error) {
      const message = resolveInviteErrorMessage(error)
      console.error(message === INVITE_FAILED_MESSAGE ? UNEXPECTED_FAILURE_LOG : KNOWN_FAILURE_LOG)
      setErrorMessage(message)
    } finally {
      setIsInviting(false)
    }
  }

  const cancel = () => {
    navigate(-1)
  }

  return (
    <>
      {errorMessage && <CsvDialog
        open={Boolean(errorMessage)}
        handleClose={() => setErrorMessage(null)}
        type={'error'}
        message={errorMessage}
      />}

      {isOpen && <CsvDialog
        open={isOpen}
        handleClose={() => {
          navigate('/settings/users', { state: { newUser } })
        }}
        type={'user'}
      />}

      <FilterSection filter={filters} onFilterChange={filterOnChange} roleValues={roleValues} emailError={emailError}/>
        <Box>
        <Button variant='outlined'
          color="error"
          sx={{ marginRight: '1rem' }}
          onClick={cancel}
          disabled={isInviting}>
        Cancel
      </Button>
      <Button
        variant='contained'
        onClick={inviteUser}
        disabled={isInviting || !isValidEmail(filters.email) || !filters.role}>
        {isInviting ? <CircularProgress sx={{ color: '#0071f4'}} size={35} color={'inherit'}/>  : 'Send invitation'}
      </Button>
        </Box>
      
    </>
  )
}

const FilterSection = ({ filter, onFilterChange, roleValues, emailError }) => {
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: '2rem',
    }}>
      <Box sx={{ marginRight: '1rem' }}>
        <BuildTextField
          value={filter.email}
          placholder={'Enter e-mail'}
          setter={(e) => {onFilterChange('email', e)}}
          disableUnderline={true}
          type={'email'}
          label={'Email'}
          required
          error={emailError}
          helperText={emailError ? INVALID_EMAIL_MESSAGE : undefined}
        />
      </Box>

      <BuildDropdown
        currentValue={filter.role}
        values={roleValues}
        isSmall
        label={'Role'}
        required
        setter={(e) => {onFilterChange('role', e)}}
        disableUnderline={true}
      />
    </Box>
  )
}
export default InviteUser
